import produce, { Draft } from 'immer';
import { getType } from 'typesafe-actions';

import { convertCropToPixelCrop, getCreativeImageCropError } from '../CreativeImageCropHelper/CreativeImageCropHelper';

import {
    changeSize,
    closeModal,
    CreativeImageCropModalAction,
    cropComplete,
    openModal,
    saveModal,
    setImageSize,
    showError
} from './actions';
import {
    getCreativeImageCropData,
    getCreativeImageCropDataFromPixelCrop,
    getCreativeImagePixelCrop,
    getDefaultCenter
} from './helpers';
import {
    selectCreativeCropData,
    selectCreativeCurrentSize,
    selectCreativeImageData,
    selectCreativeNaturalImageSize,
    selectCreativeSizeData
} from './selectors';
import { CreativeImageCropModalState } from './typings';

export const creativeImageCropModalInitialState = {
    isVisible: false,
    isLoading: false,
    error: undefined
};

export function creativeImageCropModal(
    state: CreativeImageCropModalState = creativeImageCropModalInitialState,
    action: CreativeImageCropModalAction
): CreativeImageCropModalState {
    return produce(state, (draft: Draft<CreativeImageCropModalState>) => {
        switch (action.type) {
            case getType(openModal): {
                const { cropValue, imageData, isEditMode, sizeData } = action;

                draft.imageData = imageData;
                draft.sizeData = sizeData;
                draft.isEditMode = isEditMode;
                draft.cropValue = cropValue;
                draft.isVisible = true;
                draft.isLoading = true;

                return;
            }

            case getType(setImageSize): {
                const { naturalImageSize } = action;
                const { cropValue } = draft;

                const imageData = selectCreativeImageData(draft);
                const sizeData = selectCreativeSizeData(draft);
                const { selectedSize } = sizeData;
                const currentSize = selectCreativeCurrentSize(sizeData, selectedSize);
                const pixelCrop = getCreativeImagePixelCrop(naturalImageSize, currentSize, cropValue);

                draft.cropData = {
                    [selectedSize]: getCreativeImageCropDataFromPixelCrop(naturalImageSize, pixelCrop, currentSize)
                };
                draft.defaultWidth = pixelCrop.width;
                imageData.naturalImageSize = naturalImageSize;
                draft.isLoading = false;

                return;
            }

            case getType(changeSize): {
                const { selectedSize } = action;
                const { cropValue, suggestedCenter, defaultWidth, isEditMode } = draft;

                const imageData = selectCreativeImageData(draft);
                const sizeData = selectCreativeSizeData(draft);
                const cropData = selectCreativeCropData(draft);
                const naturalImageSize = selectCreativeNaturalImageSize(imageData);

                const currentSize = selectCreativeCurrentSize(sizeData, selectedSize);

                if (!suggestedCenter && !isEditMode) {
                    const previousCrop = convertCropToPixelCrop(cropData[sizeData.selectedSize].crop, naturalImageSize);

                    draft.suggestedCenter = getDefaultCenter(previousCrop, defaultWidth);
                }

                if (!cropData[selectedSize]) {
                    cropData[selectedSize] = getCreativeImageCropData(
                        naturalImageSize,
                        currentSize,
                        cropValue,
                        draft.suggestedCenter
                    );
                }

                sizeData.selectedSize = selectedSize;

                return;
            }

            case getType(closeModal): {
                return creativeImageCropModalInitialState;
            }

            case getType(cropComplete): {
                const sizeData = selectCreativeSizeData(draft);
                const cropData = selectCreativeCropData(draft);

                cropData[sizeData.selectedSize].crop = action.crop;
                draft.error = undefined;

                return;
            }

            case getType(saveModal): {
                const { suggestedCenter, defaultWidth, isEditMode } = draft;

                const imageData = selectCreativeImageData(draft);
                const sizeData = selectCreativeSizeData(draft);
                const cropData = selectCreativeCropData(draft);
                const naturalImageSize = selectCreativeNaturalImageSize(imageData);

                if (!suggestedCenter && !isEditMode) {
                    const previousCrop = convertCropToPixelCrop(cropData[sizeData.selectedSize].crop, naturalImageSize);

                    draft.suggestedCenter = getDefaultCenter(previousCrop, defaultWidth);
                }

                draft.isLoading = true;
                draft.error = undefined;

                return;
            }

            case getType(showError): {
                draft.error = getCreativeImageCropError(action.message);
                draft.isLoading = false;

                return;
            }

            default:
                return state;
        }
    });
}
